/**
 * V4 Video Layout Component
 * ~~~***The Video-Data Dance Floor***~~~
 * 
 * Purpose: Responsive layout that integrates video viewing with v4 target analysis
 * 
 * Features:
 * - Desktop: Side-by-side video + data (resizable)
 * - Mobile: Bottom drawer video player (swipeable)
 * - Click timestamps to jump video to location
 * - Auto-play when timestamp clicked
 * - Maintains video context when switching between targets
 * 
 * Layout Strategy:
 * - >= 1024px: Split view (video left, data right)
 * - < 1024px: Stacked view with bottom drawer
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { FaExpand, FaCompress, FaPlay, FaTimes } from 'react-icons/fa';
import V4AnalysisDisplay from './V4AnalysisDisplay';
import CollapsibleVideoPlayer from '../results/display/shared/CollapsibleVideoPlayer';
import BoundingBoxOverlay from './BoundingBoxOverlay';
import TrackingOverlay from './TrackingOverlay';
import '../../styles/v4-video-layout.css';
import '../../styles/v4-tracking-overlay.css';

const V4VideoLayout = ({ 
  analysisData, 
  videoUrl, 
  videoMetadata = null,
  job = null,
  className = '' 
}) => {
  // Layout state
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  const [isVideoExpanded, setIsVideoExpanded] = useState(false); // For mobile drawer
  const [videoSplitRatio, setVideoSplitRatio] = useState(35); // Video takes 35% on desktop
  const [isDragging, setIsDragging] = useState(false);
  
  // Video control
  const [currentTarget, setCurrentTarget] = useState(null);
  const [videoTime, setVideoTime] = useState(0);
  const [currentBoundingBox, setCurrentBoundingBox] = useState(null);
  const [isTrackingEnabled, setIsTrackingEnabled] = useState(false);
  const [actualVideoElement, setActualVideoElement] = useState(null);
  const videoRef = useRef(null);
  const layoutRef = useRef(null);
  const videoElementRef = useRef(null);
  
  // Modal state - lifted here to persist across layout changes
  const [selectedModalTarget, setSelectedModalTarget] = useState(null);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'detail'

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 1024;
      setIsMobile(mobile);
      
      // Close mobile drawer when switching to desktop
      if (!mobile && isVideoExpanded) {
        setIsVideoExpanded(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isVideoExpanded]);

  // Handle timestamp click from target cards/timeline
  const handleTimestampClick = (timestamp, target = null) => {
    console.log('🎬 V4VideoLayout: Timestamp clicked!', {
      timestamp,
      targetId: target?.id,
      isMobile,
      hasVideoRef: !!videoRef.current,
      currentlyExpanded: isVideoExpanded
    });
    
    // Parse timestamp (format: "00:00:22" or "00:00:22.500")
    const timeInSeconds = parseTimestamp(timestamp);
    
    console.log('📊 Parsed timestamp:', timeInSeconds, 'seconds');
    
    if (timeInSeconds === null) {
      console.error('❌ Invalid timestamp:', timestamp);
      return;
    }

    // On mobile, expand the drawer first
    if (isMobile && !isVideoExpanded) {
      console.log('📱 Mobile detected - expanding drawer first');
      setIsVideoExpanded(true);
    }

    // Store context immediately
    setCurrentTarget(target);
    setVideoTime(timeInSeconds);
    
    // Extract bounding box data from target
    if (target) {
      const boundingBox = extractBoundingBoxAtTimestamp(target, timeInSeconds);
      setCurrentBoundingBox(boundingBox);
      console.log('📦 Bounding box extracted:', boundingBox);
    } else {
      setCurrentBoundingBox(null);
    }

    // Try to seek video with retry logic
    const attemptSeek = (retries = 0) => {
      if (!videoRef.current) {
        if (retries < 15) {
          console.log(`⏳ Video not ready, retrying... (${retries + 1}/15)`);
          setTimeout(() => attemptSeek(retries + 1), 100); // Faster retries
        } else {
          console.error('❌ Video failed to initialize after 15 retries (1.5 seconds)');
        }
        return;
      }

      try {
        console.log('⏩ Seeking video to:', timeInSeconds);
        videoRef.current.seekTo(timeInSeconds);
        
        console.log('▶️ Playing video');
        videoRef.current.play();
        
        console.log('✅ Video seek successful!');
      } catch (error) {
        console.error('❌ Video seek error:', error);
      }
    };

    // Small initial delay to allow drawer expansion animation
    setTimeout(() => attemptSeek(), 50);
  };

  // Parse timestamp string to seconds
  const parseTimestamp = (timestamp) => {
    if (!timestamp) return null;
    
    // Handle formats: "00:00:22" or "00:00:22.500" or "22s" or just seconds
    if (typeof timestamp === 'number') return timestamp;
    
    const parts = timestamp.split(':');
    if (parts.length === 3) {
      // HH:MM:SS or HH:MM:SS.mmm
      const [hours, minutes, seconds] = parts;
      return parseInt(hours) * 3600 + parseInt(minutes) * 60 + parseFloat(seconds);
    } else if (parts.length === 2) {
      // MM:SS
      const [minutes, seconds] = parts;
      return parseInt(minutes) * 60 + parseFloat(seconds);
    } else if (timestamp.endsWith('s')) {
      // "22s" format
      return parseFloat(timestamp);
    }
    
    return parseFloat(timestamp);
  };

  // Extract bounding box data for a target at a specific timestamp
  const extractBoundingBoxAtTimestamp = (target, timeInSeconds) => {
    if (!target) return null;

    // Helper: Convert coords array [left, top, right, bottom] to {x, y, width, height}
    const coordsToBox = (coords, confidence) => {
      if (!coords || coords.length < 4) return null;
      const [left, top, right, bottom] = coords;
      const width = Math.max(0, Math.min(1, right - left));
      const height = Math.max(0, Math.min(1, bottom - top));
      // Only return valid boxes (not covering entire screen)
      if (width > 0.9 || height > 0.9 || width < 0.01 || height < 0.01) return null;
      return {
        x: Math.max(0, Math.min(1, left)),
        y: Math.max(0, Math.min(1, top)),
        width,
        height,
        confidence: confidence || 0.9
      };
    };

    // Helper: Find closest box to timestamp
    const findClosestBox = (boxes) => {
      if (!boxes || boxes.length === 0) return null;
      
      let closestBox = null;
      let closestDiff = Infinity;
      
      boxes.forEach(box => {
        const boxTime = parseTimestamp(box.time || box.timestamp);
        if (boxTime === null) return;
        const diff = Math.abs(boxTime - timeInSeconds);
        if (diff < closestDiff) {
          closestDiff = diff;
          closestBox = box;
        }
      });
      
      // Only return if within 0.5 seconds
      if (closestDiff > 0.5) return null;
      return closestBox;
    };

    // For V4 data, bounding boxes might be in different places:
    // 1. target.bounding_boxes (precision analysis format with coords array)
    // 2. target.spatial.bounding_boxes (array with timestamps)
    // 3. target.attributes.bounding_box (single box)

    // Try precision analysis bounding_boxes first (most common now)
    if (target.bounding_boxes && Array.isArray(target.bounding_boxes) && target.bounding_boxes.length > 0) {
      const closestBox = findClosestBox(target.bounding_boxes);
      if (closestBox) {
        // Handle coords array format [left, top, right, bottom]
        if (closestBox.coords && Array.isArray(closestBox.coords)) {
          return coordsToBox(closestBox.coords, target.confidence);
        }
        // Handle x,y,width,height format
        if (closestBox.x !== undefined && closestBox.y !== undefined) {
          return {
            x: closestBox.x,
            y: closestBox.y,
            width: closestBox.width || 0.1,
            height: closestBox.height || 0.1,
            confidence: closestBox.confidence || target.confidence || 0.9
          };
        }
      }
    }
    
    // Try spatial bounding boxes
    if (target.spatial?.bounding_boxes && Array.isArray(target.spatial.bounding_boxes) && target.spatial.bounding_boxes.length > 0) {
      const closestBox = findClosestBox(target.spatial.bounding_boxes);
      if (closestBox) {
        if (closestBox.coords && Array.isArray(closestBox.coords)) {
          return coordsToBox(closestBox.coords, target.confidence);
        }
        if (closestBox.x !== undefined && closestBox.y !== undefined) {
          return {
            x: closestBox.x,
            y: closestBox.y,
            width: closestBox.width || 0.1,
            height: closestBox.height || 0.1,
            confidence: closestBox.confidence || target.confidence || 0.9
          };
        }
      }
    }

    // Try single bounding box in attributes
    if (target.attributes?.bounding_box) {
      const box = target.attributes.bounding_box;
      if (box.coords && Array.isArray(box.coords)) {
        return coordsToBox(box.coords, target.confidence);
      }
      if (box.x !== undefined && box.y !== undefined) {
        return {
          x: box.x,
          y: box.y,
          width: box.width || 0.1,
          height: box.height || 0.1,
          confidence: target.confidence || 0.9
        };
      }
    }

    // No valid bounding box found - return null instead of fake centered box
    return null;
  };

  // Handle resize drag (desktop only)
  const handleMouseDown = (e) => {
    if (isMobile) return;
    setIsDragging(true);
    e.preventDefault();
  };

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e) => {
      if (!layoutRef.current) return;
      
      const containerRect = layoutRef.current.getBoundingClientRect();
      const newRatio = ((e.clientX - containerRect.left) / containerRect.width) * 100;
      
      // Constrain between 25% and 60%
      const constrainedRatio = Math.max(25, Math.min(60, newRatio));
      setVideoSplitRatio(constrainedRatio);
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);

  // Mobile: Handle touch swipe for drawer
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  const handleTouchStart = (e) => {
    setTouchStart(e.touches[0].clientY);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.touches[0].clientY);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isUpSwipe = distance > 50;
    const isDownSwipe = distance < -50;
    
    if (isUpSwipe && !isVideoExpanded) {
      setIsVideoExpanded(true);
    } else if (isDownSwipe && isVideoExpanded) {
      setIsVideoExpanded(false);
    }
    
    // Reset
    setTouchStart(null);
    setTouchEnd(null);
  };

  // Close mobile drawer
  const closeMobileDrawer = () => {
    setIsVideoExpanded(false);
    if (videoRef.current) {
      videoRef.current.pause();
    }
  };

  // Get video element ref for bounding box overlay and tracking
  useEffect(() => {
    if (videoRef.current) {
      // CollapsibleVideoPlayer exposes getVideoElement() method
      const findVideoElement = () => {
        const player = videoRef.current;
        let videoEl = null;
        
        // Use the exposed getVideoElement method
        if (player && typeof player.getVideoElement === 'function') {
          videoEl = player.getVideoElement();
        }
        
        // Fallback: try to find the actual <video> element in the DOM
        if (!videoEl) {
          const videoContainer = document.querySelector('.v4-video-section');
          if (videoContainer) {
            videoEl = videoContainer.querySelector('video');
          }
        }
        
        if (videoEl) {
          videoElementRef.current = videoEl;
          setActualVideoElement(videoEl);
          console.log('📹 Video element found for tracking overlay');
        }
      };
      
      // Try immediately
      findVideoElement();
      
      // Also try after delays in case video isn't loaded yet
      const timers = [
        setTimeout(findVideoElement, 100),
        setTimeout(findVideoElement, 500),
        setTimeout(findVideoElement, 1000)
      ];
      
      return () => timers.forEach(t => clearTimeout(t));
    }
  }, [videoRef.current, videoUrl]);

  // Get target label for bounding box
  const getTargetLabel = (target) => {
    if (!target) return '';
    
    if (target.type === 'vehicle') {
      const make = target.attributes?.make || '';
      const model = target.attributes?.model || '';
      const color = target.attributes?.color || '';
      return `${color} ${make} ${model}`.trim() || 'Vehicle';
    }
    
    if (target.type === 'person') {
      return target.attributes?.description || 'Person';
    }
    
    return target.type ? target.type.charAt(0).toUpperCase() + target.type.slice(1) : 'Target';
  };

  // Check if we have precision-enhanced data with bounding boxes
  const hasPrecisionData = analysisData?.targets?.some(t => 
    t.precision_enhanced && t.bounding_boxes && t.bounding_boxes.length > 0
  );

  // Render video player section
  const renderVideoPlayer = () => (
    <div className="v4-video-section" style={{ position: 'relative' }}>
      {videoUrl ? (
        <>
          <CollapsibleVideoPlayer
            ref={videoRef}
            videoUrl={videoUrl}
            videoTitle="Evidence Video"
            videoMetadata={videoMetadata}
            defaultCollapsed={false}
            maxHeight={isMobile && isVideoExpanded ? '60vh' : '100%'}
            fitMode="contain"
            className="v4-integrated-video"
          />
          
          {/* Tracking Toggle Button - only show for precision-enhanced data */}
          {hasPrecisionData && (
            <div className="video-tracking-controls">
              <button 
                className={`tracking-toggle-btn ${isTrackingEnabled ? 'active' : ''}`}
                onClick={() => setIsTrackingEnabled(!isTrackingEnabled)}
                title={isTrackingEnabled ? 'Hide VI tracking boxes' : 'Show VI tracking boxes'}
              >
                <span className="toggle-icon">📦</span>
                <span className="toggle-text">{isTrackingEnabled ? 'TRACKING ON' : 'SHOW TRACKING'}</span>
              </button>
            </div>
          )}
          
          {/* Real-time Tracking Overlay - shows all targets as video plays */}
          {hasPrecisionData && (
            <TrackingOverlay
              targets={analysisData?.targets || []}
              videoElement={actualVideoElement}
              isEnabled={isTrackingEnabled}
              onClose={() => setIsTrackingEnabled(false)}
            />
          )}
          
          {/* Single Bounding Box Overlay - shows when timestamp clicked */}
          {currentBoundingBox && currentTarget && !isTrackingEnabled && (
            <BoundingBoxOverlay
              boundingBox={currentBoundingBox}
              targetType={currentTarget.type}
              targetLabel={getTargetLabel(currentTarget)}
              videoElement={videoElementRef.current}
              autoHideDelay={5000}
            />
          )}
        </>
      ) : (
        <div className="video-placeholder">
          <FaPlay className="placeholder-icon" />
          <p>No video available</p>
          <p className="placeholder-hint">Video will appear here for playback</p>
        </div>
      )}
      
      {/* Enhanced context banner (shown when video is playing from timestamp) */}
      {currentTarget && (
        <div className="video-context-banner">
          <div className="context-info">
            <span className="context-icon">
              {currentTarget.type === 'vehicle' ? '◆' : 
               currentTarget.type === 'person' ? '●' : 
               currentTarget.type === 'environment' ? '▲' : '■'}
            </span>
            <div className="context-details">
              <span className="context-target-type">
                {currentTarget.type === 'vehicle' && (
                  `${currentTarget.attributes?.color || ''} ${currentTarget.attributes?.make || ''} ${currentTarget.attributes?.model || ''}`.trim() || currentTarget.type
                )}
                {currentTarget.type !== 'vehicle' && currentTarget.type}
              </span>
              <span className="context-timestamp">at {formatTimestamp(videoTime)}</span>
            </div>
          </div>
          <div className="context-actions">
            {/* Replay button */}
            <button 
              className="context-action-btn"
              onClick={() => {
                if (videoRef.current) {
                  videoRef.current.seekTo(videoTime);
                  videoRef.current.play();
                }
              }}
              title="Replay from this moment"
            >
              <FaPlay style={{ fontSize: '12px' }} />
            </button>
            {/* Clear context */}
            <button 
              className="clear-context-btn"
              onClick={() => setCurrentTarget(null)}
              title="Clear context"
            >
              <FaTimes />
            </button>
          </div>
        </div>
      )}
    </div>
  );

  // Format seconds to timestamp
  const formatTimestamp = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Render data section with timestamp click handlers
  const renderDataSection = () => (
    <div className="v4-data-section">
      <V4AnalysisDisplay 
        analysisData={analysisData}
        onTimestampClick={handleTimestampClick}
        selectedModalTarget={selectedModalTarget}
        setSelectedModalTarget={setSelectedModalTarget}
        viewMode={viewMode}
        setViewMode={setViewMode}
        job={job}
      />
    </div>
  );

  // DESKTOP LAYOUT
  if (!isMobile) {
    return (
      <div 
        ref={layoutRef}
        className={`v4-video-layout desktop-layout ${className} ${isDragging ? 'dragging' : ''}`}
      >
        {/* Video Panel (Left) */}
        <div 
          className="layout-panel video-panel"
          style={{ width: `${videoSplitRatio}%` }}
        >
          {renderVideoPlayer()}
        </div>

        {/* Resize Handle */}
        <div 
          className="resize-handle"
          onMouseDown={handleMouseDown}
          title="Drag to resize"
        >
          <div className="resize-handle-bar"></div>
        </div>

        {/* Data Panel (Right) */}
        <div 
          className="layout-panel data-panel"
          style={{ width: `${100 - videoSplitRatio}%` }}
        >
          {renderDataSection()}
        </div>
      </div>
    );
  }

  // MOBILE LAYOUT with bottom drawer
  return (
    <div className={`v4-video-layout mobile-layout ${className}`}>
      {/* Main Data Area (Always visible) */}
      <div className={`mobile-data-container ${isVideoExpanded ? 'with-drawer-open' : ''}`}>
        {renderDataSection()}
      </div>

      {/* Bottom Drawer (Swipeable) */}
      <div 
        className={`mobile-video-drawer ${isVideoExpanded ? 'expanded' : 'collapsed'}`}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Drawer Handle */}
        <div className="drawer-handle" onClick={() => setIsVideoExpanded(!isVideoExpanded)}>
          <div className="drawer-handle-bar"></div>
          <div className="drawer-header">
            <div className="drawer-title">
              {isVideoExpanded ? (
                <>
                  <FaCompress /> Swipe down to close
                </>
              ) : (
                <>
                  <FaPlay /> Tap or swipe up to watch video
                </>
              )}
            </div>
          </div>
        </div>

        {/* Video Content - Always render but hide when collapsed */}
        <div className="drawer-content" style={{ display: isVideoExpanded ? 'flex' : 'none' }}>
          {renderVideoPlayer()}
          <button 
            className="close-drawer-btn"
            onClick={closeMobileDrawer}
          >
            Close Video
          </button>
        </div>
      </div>

      {/* Overlay (when drawer is open) */}
      {isVideoExpanded && (
        <div 
          className="drawer-overlay"
          onClick={closeMobileDrawer}
        />
      )}
    </div>
  );
};

export default V4VideoLayout;

